import type {
  CartCustomerPayload,
  PaymentPayload,
} from '@/api/booking/upsertCartMutation/types';
import { storage, storageKeys } from '@/constants/storage';
import type { UpsertCartPayload } from '@/hooks/mutations/booking/useUpsertCartMutation';
import { type Shop, ShopKind } from '@/types/appointment';
import type { Cart, CartItem, Service } from '@/types/booking';
import {
  getBookingChannel,
  getBookingPlatform,
} from '@/utils/bookingAttribution';

type LocalCartItem = Pick<
  CartItem,
  | 'id'
  | 'name'
  | 'services'
  | 'bookedWithAnyBarber'
  | 'barberId'
  | 'barberPasscode'
  | 'dateTime'
  | 'tipAmount'
  | 'tipPercentage'
>;

function clearEmptyValues<T extends Record<string, unknown>>(item: T): T {
  return Object.fromEntries(
    Object.entries(item).filter(([_, value]) => {
      return value !== undefined;
    }),
  ) as T;
}

/**
 * Transforms a parent ID back to the actual service ID.
 *
 * Cart items may contain parent IDs due to optimistic updates in useOptimisticCartUpdater.
 * API endpoints (schedule, cart update) expect actual service IDs, so we need to transform
 * parent IDs back to service IDs.
 *
 * @param parentId - The ID that might be a parent ID
 * @param services - The services list to look up the actual service ID
 * @returns The actual service ID if found, undefined otherwise
 */
export function getServiceIdFromParentId(
  parentId: string,
  services?: Service[],
): string | undefined {
  if (!services) return undefined;

  const service = services.find((s) => {
    if (s.parentId === parentId) {
      return true;
    }

    const assignedService = s.assignments?.find(
      (assignment) =>
        assignment.parentId === parentId || assignment.serviceId === parentId,
    );

    if (assignedService) {
      return true;
    }

    return false;
  });
  return service?.id;
}

/**
 * Transforms an array of service IDs (which may be parent IDs) to actual service IDs.
 *
 * @param serviceIds - Array of service/parent IDs to transform
 * @param services - The services list for lookup
 * @returns Array of actual service IDs
 */
export function transformServiceIds(
  serviceIds: string[],
  services?: Service[],
): string[] {
  return serviceIds.map((id) => getServiceIdFromParentId(id, services) || id);
}

/**
 * Strips an empty `preferredLanguage` from the customer details. The API
 * rejects `''` with 422, which fails the whole upsert (and, since the backend
 * matches the membership discount off these details, silently drops the
 * discount). Omitted is accepted. Applied here because every cart payload —
 * upsert, checkout and the guest validation — is built by this function, so no
 * caller can reintroduce it (SQR-15069).
 */
const toWireCustomer = (
  customer?: CartCustomerPayload,
): CartCustomerPayload | undefined => {
  if (!customer) return undefined;

  const { preferredLanguage, ...rest } = customer;
  return preferredLanguage ? { ...rest, preferredLanguage } : rest;
};

export function transformCartIntoPayloadCart(
  cart: {
    payments?: PaymentPayload[];
    customer?: CartCustomerPayload;
    promoCode?: string | null;
    promoId?: string | null;
    giftCardCode?: string | null;
    items: LocalCartItem[];
  },
  customerId?: string,
  services?: Service[],
): UpsertCartPayload {
  return {
    isBookNoPay: false,
    isReservation: false,
    payments: cart.payments || [],
    items: [],
    promoCode: cart.promoCode || null,
    promoId: cart.promoId,
    giftCardCode: cart.giftCardCode || null,
    discounts: [],
    platform: getBookingPlatform(),
    customer: toWireCustomer(cart.customer),
    new_appointments: cart.items.map((cartItem) =>
      clearEmptyValues({
        customerId,
        bookedWithAnyBarber: cartItem.bookedWithAnyBarber,
        barberId: cartItem.barberId || undefined,
        barberPasscode: cartItem.barberPasscode || undefined,
        dateTime: cartItem.dateTime || null,
        tipPercentage: cartItem.tipPercentage
          ? cartItem.tipPercentage
          : undefined,
        services: cartItem.services.map((service) => ({
          id: getServiceIdFromParentId(service.id, services) || service.id,
        })),
        channel: getBookingChannel(),
      }),
    ),
    // One tip entry per reservation, matching water-on-web format.
    tips: cart.items
      .map((cartItem, index) =>
        clearEmptyValues({
          barberId:
            cartItem.barberId || cart.items.at(index - 1)?.barberId || '',
          amount: cartItem.tipAmount,
          tipPercentage: cartItem.tipPercentage
            ? cartItem.tipPercentage
            : undefined,
        }),
      )
      .filter((tip) => !!tip.barberId),
  };
}

/**
 * Determines whether a booking requires prepayment/deposit: shop-level flags
 * are already resolved by the backend (no hierarchy to recompute), and
 * cart-item flags already carry barber > shop > brand precedence resolved
 * server-side, so this simply ORs them together.
 */
export function isPrepaymentRequired(
  shop:
    | Partial<Pick<Shop, 'requiresPrepayment' | 'requiresPartialDeposits'>>
    | null
    | undefined,
  cartItems?: Pick<CartItem, 'requiresPrepaid' | 'requiresDeposit'>[],
): boolean {
  return (
    !!shop?.requiresPrepayment ||
    !!shop?.requiresPartialDeposits ||
    (cartItems?.some((item) => item.requiresPrepaid || item.requiresDeposit) ??
      false)
  );
}

/**
 * Whether a single cart item's barber accepts tips before the appointment.
 *
 * Evaluated per item, never collapsed across the cart: in a group booking each
 * appointment can have a different barber, and one barber with tipping disabled
 * must not suppress tipping for the others. This mirrors water-on-web, which
 * renders its tip picker per sale-order item off that item's own barber
 * (`AppointmentBreakdown`: `saleOrderItem.barber?.lateTipping`).
 *
 * "Any barber" items are gated on the assigned barber once the server resolves
 * one, and offer no tipping before that — water-on-web's
 * `ReservationModel.isEarlyTippingEnabled` reads the any-barber candidate's
 * `earlyTipping` and returns false while no candidate exists. The waitlist is
 * the only surface with an any-barber bypass, there it does mirror
 * water-on-web (`useWaitingListTips`).
 */
export function canTipCartItem(
  item: Pick<CartItem, 'bookedWithAnyBarber' | 'barberId' | 'barber'>,
): boolean {
  return item.barber?.earlyTipping === true;
}

export function validateCartItems(
  items: Pick<
    CartItem,
    | 'id'
    | 'name'
    | 'services'
    | 'bookedWithAnyBarber'
    | 'barberId'
    | 'dateTime'
    | 'tipAmount'
    | 'tipPercentage'
  >[],
  shop?: Shop,
) {
  if (shop?.kind === ShopKind.Rental) {
    const barberId = items.at(0)?.barberId;
    if (!items.every((item) => item.barberId === barberId)) {
      throw new Error(
        'Rental shops must have all items booked with the same barber',
      );
    }
  }
}

export function getCartItemTotal(
  cartItem: CartItem,
  countTipInPrice = false,
  selectedTipAmount?: number,
): number {
  return cartItem.subItems?.reduce(
    (acc, subItem) => acc + subItem.cost,
    countTipInPrice ? (selectedTipAmount ?? 0) : 0,
  );
}

export function getCartTotal(
  cart: Cart | null,
  countTipInPrice = false,
): number {
  if (!cart) return 0;

  return cart.items.reduce(
    (acc, item) =>
      acc + getCartItemTotal(item, countTipInPrice, item.tipAmount),
    0,
  );
}

/**
 * Returns the cart's range display price from breakdown if it represents a range
 * (i.e., min !== max). Returns null if there's no range or no breakdown.
 */
export function getCartRangePrice(
  cart: Cart | null,
): { min: number; max: number } | null {
  if (!cart?.breakdown?.rangeDisplayPrice) return null;

  const { min, max } = cart.breakdown.rangeDisplayPrice;
  if (min === max) return null;

  return { min, max };
}

/** `barber` is set once a professional is assigned, so its price is exact. */
export function isAnyProfessionalCartItem(cartItem: CartItem): boolean {
  return cartItem.bookedWithAnyBarber && !cartItem.barber;
}

export function hasSubItemPriceRange(
  subItem: CartItem['subItems'][number],
): boolean {
  return (
    !!subItem.rangeDisplayPrice &&
    subItem.rangeDisplayPrice.min !== subItem.rangeDisplayPrice.max
  );
}

export function cartHasAnyProfessionalPriceRange(cart: Cart | null): boolean {
  if (!cart || !getCartRangePrice(cart)) return false;

  return cart.items.some(
    (item) =>
      isAnyProfessionalCartItem(item) &&
      item.subItems?.some(hasSubItemPriceRange),
  );
}

/**
 * Returns the cart item's range display price if it represents a range.
 * Calculated from sub-items' rangeDisplayPrice fields.
 */
export function getCartItemRangePrice(
  cartItem: CartItem,
): { min: number; max: number } | null {
  if (!cartItem.subItems?.length) return null;

  const hasRange = cartItem.subItems.some(
    (si) =>
      si.rangeDisplayPrice &&
      si.rangeDisplayPrice.min !== si.rangeDisplayPrice.max,
  );

  if (!hasRange) return null;

  return {
    min: cartItem.subItems.reduce(
      (acc, si) => acc + (si.rangeDisplayPrice?.min ?? si.cost),
      0,
    ),
    max: cartItem.subItems.reduce(
      (acc, si) => acc + (si.rangeDisplayPrice?.max ?? si.cost),
      0,
    ),
  };
}

/**
 * squire-api fuzzy-matches a cart's contact details and ignores the session, so
 * they're sent only for a guest with no known customer id (SQR-15987).
 */
export const omitKnownCustomerDetails = <T extends { customer?: unknown }>(
  payload: T,
  customerId?: string,
): T =>
  customerId || storage.getString(storageKeys.authToken)
    ? { ...payload, customer: undefined }
    : payload;
